import { FunctionComponent, useEffect, useState } from "react";
import { View } from "react-native";
import { useHistory } from "react-router-dom";

import TourHelpPage from "@/components/TourHelpPage";
import FullScreenLoader from "@/components/common/FullScreenLoader";
import { StateType, useAppState } from "@/context/AppState";
import { useParams } from "@/hooks/queryParams";
import {
  ConfigType,
  ProspectInfoConfigType,
  ProspectType,
  TourType,
} from "@/types";
import { clearStorage } from "@/utils/storage";
import { fetchTourAndProspect } from "@/utils/tour";

import NotFound from "../NotFound";

/**
 * A higher order component that requires the full tour and prospect
 * to be loaded in order to render the wrapped component.
 *
 * Requires `accessToken` to be included in props, which
 * makes it work nicely with RequireAuthentication.
 *
 * The tour will _not_ be loaded unless it's already associated with
 * a prospect.
 */
export default function RequireTourAndProspect(
  WrappedComponent: FunctionComponent<{
    accessToken: string;
    tour: TourType;
    config: ConfigType;
    prospect: ProspectType;
    prospectInfoConfig: ProspectInfoConfigType;
  }>,
  // Used to determine which pages will show the help page if the tour is finalized
  options = { allowFinalizedTours: false }
): FunctionComponent<{ accessToken: string }> {
  return function RequireTourAndProspectComponent(props: {
    accessToken: string;
  }) {
    const { tourToken } = useParams();
    const [appState, setAppState] = useAppState();
    const { accessToken } = props;
    const tourAndProspectLoaded = appState.tour && appState.prospect;
    const history = useHistory();

    const [requestSent, setRequestSent] = useState<boolean>(false);
    const [tourOrProspectNotFound, setTourOrProspectNotFound] =
      useState<boolean>(false);

    // Immediately attempt to fetch the tour and prospect
    useEffect(() => {
      if (tourAndProspectLoaded) {
        // We've already loaded the tour and prospect.
        //
        // We might have loaded the "public" version of the tour,
        // which does not expose the prospect, and this component
        // is supposed to ensure we have the "full" version of the
        // tour, so `if (appState.tour)` may look faulty because it
        // doesn't ensure we have the "full" version. But by checking
        // that the prospect is loaded, we implicitly ensure that the
        // full version of the tour is loaded, too.
        return;
      }

      if (requestSent) {
        // Don't issue a 2nd request
        return;
      }

      setRequestSent(true);

      fetchTourAndProspect({ tourToken, accessToken })
        .then(({ tour, prospect }) => {
          if (!tour || !prospect) {
            // The API response failed, show not found component
            setTourOrProspectNotFound(true);
          }

          setAppState((appState: StateType) => ({
            ...appState,
            tour,
            prospect,
          }));
        })
        .catch((error) => {
          if (error?.response?.status === 403) {
            clearStorage();
            window.location.href = history.location.pathname;
          }
        });
    }, [
      requestSent,
      accessToken,
      history,
      tourToken,
      tourAndProspectLoaded,
      setAppState,
    ]);

    if (tourOrProspectNotFound) {
      return (
        <View
          style={{
            width: "100%",
            height: "100%",
            flex: 1,
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <NotFound />
        </View>
      );
    }

    // We may well already have the tour and prospect, in which case we'll
    // go ahead and render the wrapped component
    if (!appState.tour || !appState.prospect) {
      return <FullScreenLoader />;
    }

    // If user reschedules, a new tour is made so we need to ensure that the tourToken
    // that is in appState is relevant to prevent pages from displaying for wrong tours.
    if (appState.tour?.token !== tourToken) {
      return <FullScreenLoader />;
    }

    if (
      appState.tour.deletedAt ||
      appState.tour.identityVerificationResult === "rejected"
    ) {
      // Show when the tour is deleted on any routes that use this higher level component
      return <TourHelpPage tour={appState.tour} />;
    }
    // This case shows on routes where tour is finalized, except on the actual tours page
    if (!options.allowFinalizedTours && appState.tour.isFinalized) {
      return <TourHelpPage tour={appState.tour} />;
    }

    return (
      <WrappedComponent
        {...props}
        tour={appState.tour}
        config={appState.config}
        prospect={appState.prospect}
        prospectInfoConfig={appState.prospectInfoConfig}
      />
    );
  };
}
